/**
 * 65.16's bot rule (lane B): what a bot carrying strike drones sends one at — only what its team knows (the one accessor,
 * actionKnowledge.ts teamKnowledge), never the truth. In order:
 *   1. a gun crew: a known enemy at an enemy emplacement (within STRIKE_CREW_PEOPLE person heights of it);
 *   2. a cluster: the known enemy with the most other known enemies inside the burst (two or more in all).
 * Never when a teammate stands inside the burst at the aim, nor beyond the drone's one-way reach, nor at anything older
 * than STRIKE_MIN_CONFIDENCE. A single enemy in the open is the rifle's. Pure.
 */
import type { GameState, PlayerState, StrikeTarget, Vec3 } from '../types';
import { PERSON_BODY_HEIGHT } from '../constants';
import { vecLength, vecNormalize, vecSub } from '../math';
import { getPlayerWorldPosition, getStructureWorldPosition } from '../core/gameState';
import { personSetOf } from '../core/scaleProfile';
import { strikeBlast } from '../core/strikeDrone';
import { knownEnemies, type KnownEnemy } from './actionKnowledge';

/** A known enemy this near an enemy emplacement is its crew (person heights). */
export const STRIKE_CREW_PEOPLE = 2;
/** Below this a belief is too stale to spend the charge on. */
export const STRIKE_MIN_CONFIDENCE = 0.5;

/** The strike for `unit` from what its team knows (`known`, on the unit's planet), or null. */
export function strikeTargetFor(state: GameState, unit: PlayerState, known: readonly KnownEnemy[], rule?: { crewPeople?: number; minConfidence?: number }): StrikeTarget | null {
  const ps = personSetOf(state);
  const k = crewOrCluster(state, unit, known, { burst: strikeBlast(state).radius, reach: ps.droneSpeed * ps.droneEndurance, ...rule });
  return k ? { unitId: k.unitId! } : null;
}

/**
 * The known enemy worth a burst of radius `burst` from `unit`, within `reach` (and, with `minRange`, no nearer than that
 * — a grenade must not catch its thrower): a gun crew first, else the densest cluster (two or more); with `cantHit` (the
 * grenade's), else the nearest it cannot shoot — behind cover, or lying low at a post (the bots spread ~4 people apart, so
 * a cluster seldom forms: supervisor-1's sweep saw no HE thrown in 648 skirmishes); null otherwise. The strike drone's
 * rule and the HE grenade's (65.15) share it. With `hiddenFresh` (69.85 (a)), the third only on a belief made within
 * `ticks` of `now`: the crew and the cluster still read the team's whole picture.
 */
export function crewOrCluster(state: GameState, unit: PlayerState, known: readonly KnownEnemy[], opts: { burst: number; reach: number; minRange?: number; crewPeople?: number; minConfidence?: number; cantHit?: (k: KnownEnemy) => boolean; hiddenFresh?: { now: number; ticks: number } }): KnownEnemy | null {
  const ps = personSetOf(state);
  const planet = state.planets[unit.planetIndex];
  if (!planet) return null;
  const { burst, reach } = opts;
  const minRange = opts.minRange ?? 0;
  const from = getPlayerWorldPosition(unit, state.planets);
  const arc = (p: Vec3) => {
    const a = vecNormalize(vecSub(p, planet.position)), b = vecNormalize(vecSub(from, planet.position));
    return vecLength(vecSub(a, b)) * planet.radius;   // the chord: within a hair of the arc at these distances
  };
  // (one belief per enemy: the team's knowledge lists an enemy once a source — seen and told — and a cluster counted it twice,
  // so one enemy in sight and in the picture drew a grenade or a strike even in the open)
  const live = knownEnemies(known).filter(k => k.unitId !== undefined && (k.planetIndex === undefined || k.planetIndex === unit.planetIndex)
    && k.confidence >= (opts.minConfidence ?? STRIKE_MIN_CONFIDENCE) && arc(k.pos) <= reach && arc(k.pos) >= minRange);
  const friendNear = (p: Vec3) => state.players.some(q => q.alive && q.teamIndex === unit.teamIndex && q.planetIndex === unit.planetIndex
    && vecLength(vecSub(getPlayerWorldPosition(q, state.planets), p)) <= burst);
  // 1. a gun crew: a known enemy by an enemy emplacement (a gun stands in the open; its crew is who the team saw there)
  const crewReach = (opts.crewPeople ?? STRIKE_CREW_PEOPLE) * PERSON_BODY_HEIGHT * ps.k;
  const enemyGuns = state.structures.filter(s => s.seats?.length && s.health > 0 && s.planetIndex === unit.planetIndex
    && state.players.find(p => p.id === s.ownerId)?.teamIndex !== unit.teamIndex);
  for (const g of [...enemyGuns].sort((a, b) => a.id - b.id)) {
    const at = getStructureWorldPosition(g, state.planets);
    const crew = live.filter(k => vecLength(vecSub(k.pos, at)) <= crewReach).sort((a, b) => a.unitId! - b.unitId!)[0];
    if (crew && !friendNear(crew.pos)) return crew;
  }
  // 2. a cluster: the known enemy with the most known enemies inside the burst round it (ties: the lower id)
  let best: { k: KnownEnemy; n: number } | null = null;
  for (const k of [...live].sort((a, b) => a.unitId! - b.unitId!)) {
    const n = live.filter(o => vecLength(vecSub(o.pos, k.pos)) <= burst).length;
    if (n >= 2 && (!best || n > best.n) && !friendNear(k.pos)) best = { k, n };
  }
  if (best) return best.k;
  // 3. (a grenade) the nearest known enemy it cannot hit with a shot: in cover, or down behind it (ties: the lower id)
  if (opts.cantHit) {
    const fresh = opts.hiddenFresh;
    const hidden = live.filter(k => (!fresh || fresh.now - k.tick <= fresh.ticks) && !friendNear(k.pos) && opts.cantHit!(k)).sort((a, b) => arc(a.pos) - arc(b.pos) || a.unitId! - b.unitId!);
    if (hidden.length) return hidden[0];
  }
  return null;
}
